/**
 * 抽壳特征预览执行处理器。
 */

import { ShellFeatureDefinitionModel } from '../../../definition/shell/ShellFeatureDefinition';
import type { FeatureEditSession } from '../../../edit/FeatureEditSession';
import { ShellFeaturePrototype } from '../../../prototype/shell/ShellFeaturePrototype';
import {
    FeaturePreviewExecutionHandler,
    type FeaturePreviewExecutionInput,
    type FeaturePreviewExecutionResult,
} from '../FeaturePreviewExecutionHandler';
import { ShellPreviewExecution } from './ShellPreviewExecution';

/**
 * 抽壳特征预览执行处理器。
 */
export class ShellPreviewExecutionHandler extends FeaturePreviewExecutionHandler {
    /** 抽壳预览执行器。 */
    private readonly execution: ShellPreviewExecution;

    /**
     * @param execution 抽壳预览执行器。
     */
    public constructor(execution: ShellPreviewExecution) {
        super();
        this.execution = execution;
    }

    /**
     * @inheritdoc
     */
    public supports(input: FeaturePreviewExecutionInput): boolean {
        return input.featureKind === 'shell' && input.definition instanceof ShellFeatureDefinitionModel;
    }

    /**
     * @inheritdoc
     * @throws 当预览输入中的定义不是抽壳特征定义时。
     */
    public async execute(input: FeaturePreviewExecutionInput): Promise<FeaturePreviewExecutionResult> {
        if (!(input.definition instanceof ShellFeatureDefinitionModel)) {
            throw new Error('抽壳预览处理器收到的 definition 类型不正确');
        }

        const prototype = new ShellFeaturePrototype({
            featureId: input.featureId ?? `preview:${Date.now()}`,
            definition: input.definition,
            bindings: input.bindings,
            sourceSketchId: null,
        });
        const previewResult = input.session
            ? await this.execution.executeEditPreview(
                input.session as FeatureEditSession<ShellFeatureDefinitionModel, ShellFeaturePrototype>,
                input.definition,
            )
            : await this.execution.executeCreatePreview(input.definition);
        return {
            meshData: previewResult.meshData,
            prototype,
            draftDefinition: input.definition,
        };
    }
}
